import apiService from './api';
import type { ApiResponse } from '../types';

/**
 * An organisation's one connection to its own Xero.
 *
 * Payroll and invoicing share it: one sign-in, one Disconnect. Nothing here
 * chooses a Xero organisation: the sign-in connects the one selected on Xero's
 * own screen, to the organisation the person is in. What each then does with
 * it is on its own service: `xeroPayroll.ts` (employee links, leave) and
 * `nurseryAccounts.ts` (account mapping, sync). They used to hold a sign-in each, which asked the
 * same question twice.
 *
 * Not the `/xero` routes in `subscription.ts`: that is FactreeTech's own
 * billing connection.
 *
 * Backend: HHNodeServer `routes/xeroConnection.js` + `services/xeroConnectionService.js`.
 */

export interface XeroConnectionStatus {
  /** False on a test server: Xero only runs in production. */
  enabled_here: boolean;
  connected: boolean;
  /** False only for a sign-in from before it settled the Xero organisation itself: reconnect. */
  tenant_selected: boolean;
  tenant_id: string | null;
  tenant_name: string | null;
  xero_user_name: string | null;
  connected_at: string | null;
  /**
   * The two halves of what one sign-in grants. A connection made before
   * payroll and invoicing shared one holds only the half it was made for,
   * and signing in again adds the other.
   */
  invoicing: boolean;
  payroll: boolean;
  /** False when the grant predates payroll posting: reconnect to post. */
  can_post: boolean;
  missing_scopes: string[];
  /**
   * Payments, bank transfers and Receive / Spend Money can be written: what
   * tills and counter payments send. Absent from an older server.
   */
  tills?: boolean;
  missing_till_scopes?: string[];
}

class XeroConnectionService {
  async getStatus(): Promise<ApiResponse<XeroConnectionStatus>> {
    return apiService.get('/xero-connection/status');
  }

  /**
   * `remittances` also asks Xero for the permissions posting Bunnings
   * remittances needs (payments and bank transfers). An organisation with that
   * switched on is asked for them on every sign-in anyway.
   */
  async getAuthorizeUrl(remittances = false, cardPurchases = false, tills = false): Promise<ApiResponse<{ authorization_url: string }>> {
    const params = new URLSearchParams();
    if (remittances) params.set('remittances', 'true');
    // Card purchases: Spend Money and attachments (Purchase orders on Accounts → Xero).
    if (cardPurchases) params.set('card_purchases', 'true');
    // Tills and counter payments: payments and bank transactions. An
    // organisation with a till or counter account chosen is asked anyway.
    if (tills) params.set('tills', 'true');
    const qs = params.toString();
    return apiService.get(`/xero-connection/authorize${qs ? `?${qs}` : ''}`);
  }

  /**
   * Finish the sign-in Xero sent the browser back from. Only the person who
   * started it can, in the organisation they started it for.
   */
  async completeSignIn(code: string, state: string): Promise<ApiResponse<XeroConnectionStatus>> {
    return apiService.post('/xero-connection/complete', { code, state }, undefined, { timeout: 30_000 });
  }

  async disconnect(): Promise<ApiResponse<XeroConnectionStatus>> {
    return apiService.post('/xero-connection/disconnect', {});
  }
}

export const xeroConnectionService = new XeroConnectionService();
export default xeroConnectionService;
